import { supabase } from './supabase'
import { inferLabTestCategory, normalizeLabTestName } from './lab-results'
import type { AnalyseResultatInput, DocumentAnalysisResult } from '@/types/health'

type ExtractedAnalysis = DocumentAnalysisResult['analyses'][number]

function normalizeCatalogText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function buildAnalysisMetadata(analysis: ExtractedAnalysis, analysisResult: DocumentAnalysisResult) {
  const canonicalName = normalizeLabTestName(analysis.nom_analyse)

  return {
    raw_name: analysis.nom_analyse,
    canonical_name: canonicalName,
    normalized_search: normalizeCatalogText(analysis.nom_analyse),
    raw_value: analysis.valeur || null,
    unit: analysis.unite || null,
    reference_range: analysis.valeur_reference || null,
    status: analysis.statut || null,
    test_category: inferLabTestCategory(canonicalName),
    source_document_type: analysisResult.type_document,
    laboratory_name: analysisResult.laboratoire || null,
    doctor_name: analysisResult.medecin || null
  }
}

export function buildAnalyseResultatRows(params: {
  documentId: string
  patientId: string
  analysisDate: string
  analysisResult: DocumentAnalysisResult
}): AnalyseResultatInput[] {
  const { documentId, patientId, analysisDate, analysisResult } = params

  return analysisResult.analyses.map((analysis) => ({
    document_id: documentId,
    patient_id: patientId,
    nom_analyse: analysis.nom_analyse,
    valeur: analysis.valeur || null,
    unite: analysis.unite || null,
    valeur_reference: analysis.valeur_reference || null,
    statut: analysis.statut || null,
    date_analyse: analysisDate,
    laboratoire: analysisResult.laboratoire,
    commentaire: null,
    metadata: buildAnalysisMetadata(analysis, analysisResult)
  }))
}

export async function syncPatientLabTestCatalog(params: {
  documentId: string
  patientId: string
  analysisDate: string
  analysisResult: DocumentAnalysisResult
}) {
  const { documentId, patientId, analysisDate, analysisResult } = params

  if (analysisResult.analyses.length === 0) {
    return
  }

  const rows = analysisResult.analyses.map((analysis) => {
    const metadata = buildAnalysisMetadata(analysis, analysisResult)

    return {
      patient_id: patientId,
      raw_name: metadata.raw_name,
      canonical_name: metadata.canonical_name,
      normalized_search: metadata.normalized_search,
      test_category: metadata.test_category,
      last_unit: metadata.unit,
      last_reference_range: metadata.reference_range,
      last_value: metadata.raw_value,
      last_status: metadata.status,
      last_analysis_date: analysisDate,
      source_document_id: documentId,
      laboratory_name: metadata.laboratory_name,
      doctor_name: metadata.doctor_name,
      metadata
    }
  })

  const { error } = await supabase
    .from('patient_lab_test_catalog')
    .upsert(rows, { onConflict: 'patient_id,canonical_name,raw_name' })

  if (!error) {
    return
  }

  const message = error.message || ''
  const isMissingTable =
    /relation .*patient_lab_test_catalog.* does not exist/i.test(message) ||
    /Could not find the table/i.test(message)

  if (!isMissingTable) {
    console.warn('Impossible de synchroniser le catalogue des molecules:', message)
  }
}
